/**
 * /constitution — active Constitution overview.
 *
 * Two panes:
 *   1. Header strip with the active doc version + last-changed timestamp.
 *   2. A grouped list of rules with severity chip, immutability flag,
 *      search box, and a filter by category (severity).
 *
 * Founder + admin see an "Edit" button that links to /constitution/edit
 * (proposal form). Other roles are read-only.
 */

import type { ConstitutionRule, ConstitutionSeverity } from '@chaos/types/constitution';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Lock, Plus, Scale, Search, ShieldAlert, Sparkles } from 'lucide-react';
import * as React from 'react';
import { Input } from '../../../components/ui/input';
import { constitutionApi, constitutionKeys } from '../../../lib/api/constitution';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth-store';

const SEVERITY_ORDER: ConstitutionSeverity[] = [
  'critical',
  'high',
  'medium',
  'low',
  'informational',
];

const SEVERITY_LABEL: Record<ConstitutionSeverity, string> = {
  critical: 'Immutable',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  informational: 'Info',
};

const SEVERITY_CLASS: Record<ConstitutionSeverity, string> = {
  critical: 'border-destructive/40 bg-destructive/10 text-destructive-foreground',
  high: 'border-warning/40 bg-warning/10 text-warning-foreground',
  medium: 'border-border bg-muted text-muted-foreground',
  low: 'border-border bg-muted text-muted-foreground',
  informational: 'border-border bg-muted text-muted-foreground/80',
};

function ConstitutionHome() {
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const canEdit = role === 'founder';

  const rulesQ = useQuery({
    queryKey: constitutionKeys.rules('global'),
    queryFn: () => constitutionApi.listRules('global'),
    staleTime: 30_000,
  });

  const [query, setQuery] = React.useState('');
  const [severityFilter, setSeverityFilter] = React.useState<ConstitutionSeverity | 'all'>('all');

  const filtered = React.useMemo(() => {
    const rules: ConstitutionRule[] = rulesQ.data?.rules ?? [];
    const q = query.trim().toLowerCase();
    return rules.filter((r) => {
      if (severityFilter !== 'all' && r.severity !== severityFilter) return false;
      if (q.length === 0) return true;
      return (
        r.ruleKey.toLowerCase().includes(q) ||
        r.statement.toLowerCase().includes(q) ||
        (r.rationale ?? '').toLowerCase().includes(q)
      );
    });
  }, [rulesQ.data, query, severityFilter]);

  const grouped = React.useMemo(() => {
    const buckets: Record<ConstitutionSeverity, ConstitutionRule[]> = {
      critical: [],
      high: [],
      medium: [],
      low: [],
      informational: [],
    };
    for (const rule of filtered) buckets[rule.severity].push(rule);
    return buckets;
  }, [filtered]);

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-baseline gap-3">
        <Scale className="h-6 w-6 text-foreground" aria-hidden />
        <div className="flex flex-col">
          <h1 className="text-[18px] font-semibold tracking-tight text-foreground">Constitution</h1>
          <p className="text-[12px] text-muted-foreground">
            Immutable + mutable rules every agent prompt is checked against.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {rulesQ.data?.activeDocVersion ? (
            <span className="rounded border border-border bg-muted/40 px-2 py-0.5 font-mono text-[11px] tabular-nums text-muted-foreground">
              v{rulesQ.data.activeDocVersion}
            </span>
          ) : null}
          {canEdit ? (
            <Link
              to="/constitution/edit"
              className="inline-flex items-center gap-1 rounded border border-border bg-background px-2.5 py-1 text-[12px] text-foreground transition-colors hover:bg-foreground/5"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Propose rule
            </Link>
          ) : null}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search rules…"
            className="h-8 pl-8"
          />
        </div>
        <SeverityFilter value={severityFilter} onChange={setSeverityFilter} />
      </div>

      <div className="flex flex-col gap-4">
        {SEVERITY_ORDER.map((sev) => {
          const rules = grouped[sev];
          if (rules.length === 0) return null;
          return (
            <section
              key={sev}
              aria-labelledby={`sev-${sev}`}
              className="rounded border border-border bg-background"
            >
              <header className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-1.5">
                <h2
                  id={`sev-${sev}`}
                  className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
                >
                  {sev === 'critical' ? (
                    <Lock className="h-3 w-3" aria-hidden />
                  ) : (
                    <ShieldAlert className="h-3 w-3" aria-hidden />
                  )}
                  {SEVERITY_LABEL[sev]}
                </h2>
                <span className="font-mono text-[10px] tabular-nums text-muted-foreground/70">
                  {rules.length}
                </span>
              </header>
              <ul className="divide-y divide-border">
                {rules.map((rule) => (
                  <RuleRow key={rule.id} rule={rule} />
                ))}
              </ul>
            </section>
          );
        })}

        {rulesQ.isLoading ? (
          <p className="rounded border border-dashed border-border bg-muted/20 p-4 text-center text-[12px] text-muted-foreground">
            Loading rules…
          </p>
        ) : null}
        {!rulesQ.isLoading && filtered.length === 0 ? (
          <p className="rounded border border-dashed border-border bg-muted/20 p-6 text-center text-[12px] text-muted-foreground">
            {(rulesQ.data?.rules.length ?? 0) === 0
              ? 'No rules have been ratified yet.'
              : 'No rules match your filter.'}
          </p>
        ) : null}
      </div>

      <footer className="rounded border border-border bg-background p-4 text-[12px] text-muted-foreground">
        Rules marked <Sparkles className="inline h-3 w-3" aria-hidden /> Immutable are part of the
        constitutional core — they can only be changed via founder approval. NeMo Guardrails
        regenerates Colang flows automatically when a rule is ratified.
      </footer>
    </section>
  );
}

function RuleRow({ rule }: { rule: ConstitutionRule }) {
  return (
    <li className="flex flex-col gap-1 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] text-muted-foreground">{rule.ruleKey}</span>
        <span
          className={cn(
            'rounded border px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider',
            SEVERITY_CLASS[rule.severity],
          )}
        >
          {SEVERITY_LABEL[rule.severity]}
        </span>
        {rule.immutable ? (
          <span
            className="inline-flex items-center gap-0.5 rounded border border-destructive/30 bg-destructive/5 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-destructive-foreground"
            title="Immutable rule — founder approval required to change"
          >
            <Lock className="h-2.5 w-2.5" aria-hidden /> Locked
          </span>
        ) : null}
        <span className="ml-auto font-mono text-[10px] tabular-nums text-muted-foreground/70">
          {new Date(rule.createdAt).toISOString().slice(0, 10)}
        </span>
      </div>
      <p className="text-[13px] text-foreground">{rule.statement}</p>
      {rule.rationale ? (
        <p className="text-[12px] text-muted-foreground">{rule.rationale}</p>
      ) : null}
    </li>
  );
}

interface FilterProps {
  value: ConstitutionSeverity | 'all';
  onChange: (next: ConstitutionSeverity | 'all') => void;
}

function SeverityFilter({ value, onChange }: FilterProps) {
  return (
    <div className="flex items-center gap-0.5 rounded border border-border bg-background p-0.5 text-[11px]">
      <FilterChip active={value === 'all'} onClick={() => onChange('all')} label="All" />
      {SEVERITY_ORDER.map((sev) => (
        <FilterChip
          key={sev}
          active={value === sev}
          onClick={() => onChange(sev)}
          label={SEVERITY_LABEL[sev]}
        />
      ))}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded px-2 py-0.5 transition-colors',
        active
          ? 'bg-foreground/10 text-foreground'
          : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

export const Route = createFileRoute('/_authenticated/constitution/')({
  component: ConstitutionHome,
});
